import React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { NavigateFunction, useNavigate } from 'react-router-dom'

import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  getConstanceSetting,
  getUser,
} from '@disco/state-management/selectors/user'
import { isTrialExpired } from '@disco/utils/trial'

import type { User } from 'types/user'

import PricingModal from 'components/modals/pricing'

import TrialExpiredModal from 'pages/pricing/trial-expired-modal'

const mapStateToProps = state => {
  return {
    user: getUser(state),
    isGrowthTrialSignup: Boolean(
      getConstanceSetting(state, 'CB-59-GROWTH-AT-EXPIRED-TRIAL'),
    ),
  }
}

const mapDispatchToProps = {
  sendMixpanelEvent,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

const interval = 5 * 60 * 1000 // annoy user every 5 min

/**
 * TrialWatcher component for monitoring trial expiration and showing upgrade prompts
 *
 * A React class component that monitors trial status and automatically displays
 * pricing modals when trials expire. Implements periodic checking with interval-based
 * nudging to encourage plan upgrades. Includes path-based exclusions and analytics
 * tracking for trial expiration events.
 *
 * @param props - Component props
 * @param props.user - Current user object with trial information
 * @param props.sendMixpanelEvent - Function to send analytics events
 * @param props.navigate - React Router navigate function for navigation
 * @returns JSX element containing pricing modal when trial expired, or null
 */
type PropsFromRedux = ConnectedProps<typeof connector>

type Props = PropsFromRedux & {
  navigate: NavigateFunction
}

class TrialWatcher extends React.Component<
  Props,
  {
    showPricing: boolean
  }
> {
  state = {
    showPricing: false,
  }
  timer: ReturnType<typeof setInterval> | null | undefined
  _startNudge = () => {
    this.setState({
      showPricing: true,
    })
    this.props.sendMixpanelEvent(
      'Open Billing via trial prompt (trial expired)',
    )
    this.timer = setInterval(() => {
      // clear if business moved to paid plan
      if (!isTrialExpired(this.props.user)) {
        clearInterval(this.timer ?? undefined)
        return
      }

      const allowedPath =
        document.location.pathname.indexOf('/pricing') === -1 &&
        document.location.pathname.indexOf('/referral-invite') === -1

      if (!this.state.showPricing && allowedPath) {
        this.setState({
          showPricing: true,
        })
        this.props.sendMixpanelEvent(
          'Open Billing via trial prompt (trial expired)',
        )
      }
    }, interval)
  }

  componentDidMount() {
    if (!this.props.user.loggedIn) return

    if (isTrialExpired(this.props.user)) {
      this._startNudge()
    }
  }

  componentWillUnmount() {
    if (this.timer) clearInterval(this.timer)
  }

  // not possible to close
  // _onClose = () => this.setState({ showPricing: false })
  render() {
    if (!this.props.user.loggedIn) return null
    if (!isTrialExpired(this.props.user)) return null

    if (this.state.showPricing) {
      if (document.location.pathname.indexOf('/pricing') >= 0) {
        return null
      }

      if (this.props.isGrowthTrialSignup) return <TrialExpiredModal />

      return <PricingModal onClose={undefined} showBillingSummary />
    } else {
      return null
    }
  }
}

const ConnectedTrialWatcher = connector(TrialWatcher)

function TrialWatcherWithNavigate(props) {
  const navigate = useNavigate()
  return <ConnectedTrialWatcher {...props} navigate={navigate} />
}

export default TrialWatcherWithNavigate
